import React from "react";
import {
  Avatar,
  AvatarSize,
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Toggle,
} from "@hid-galaxy-ui/galaxy-react";
import { UserListType } from "../../../redux/slices/user/Types";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { toggleUserEnableSagaAction } from "../../../redux/saga/user/saga-actions";
import UserStatusBadge from "../../../pages/hid-admin/dashboard/user-management/components/UserStatusBadge";
import "./PartnerUserCard.scss";
import { usePartnerTabPermission } from "../../../contexts/PartnerPermissionContext";

const PartnerUserCard: React.FC<{
  user: UserListType[0];
  onEditUserClick: (user: UserListType[0]) => void;
  onInvite?: (user: UserListType[0]) => void;
  inviteLoading?: boolean;
}> = ({ user, onEditUserClick, onInvite, inviteLoading = false }) => {
  const [toggled, setToggled] = React.useState(user.status === "Enable");
  const { toggleUserEnableID, status } = useAppSelector((state) => state.user);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const [disabled, setDisabled] = React.useState<boolean>(
    loggedUserDetail.ID === user.ID
  );
  const dispatch = useAppDispatch();
  const isPartnerInactive = partnerDetails?.status === "Inactive";
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const showUserActions = !isPartnerInactive;
  const showInvite =
    user.status === "Invited" && onInvite != null;

  React.useEffect(() => {
    if (toggleUserEnableID === user.ID) {
      setDisabled(status.toggleUserEnableLoading);
    }
  }, [status.toggleUserEnableLoading, toggleUserEnableID, user.ID]);

  React.useEffect(() => {
    if (!disabled && toggleUserEnableID === user.ID) {
      if (user.status === "Enable" && !toggled) {
        setToggled(true);
      } else if (user.status !== "Enable" && toggled) {
        setToggled(false);
      }
    }
  }, [disabled, user, toggleUserEnableID, toggled]);

  const handleToggle = (e: any) => {
    const isChecked = e.target.checked;
    setToggled(isChecked);
    dispatch(
      isChecked
        ? toggleUserEnableSagaAction({
            ID: user.ID,
            userEmail: user.email,
            enable: 1,
          })
        : toggleUserEnableSagaAction({
            ID: user.ID,
            userEmail: user.email,
            enable: 0,
          })
    );
  };

  return (
    <div className="partner-user-card">
      <div className="info">
        <Avatar size={AvatarSize.Large} />
        <div className="name">{`${user.first_name} ${user.last_name}`}</div>
        <div className="email">{user.email}</div>
        <div className="role">{user.role}</div>
        <div className="status">
          <UserStatusBadge status={user.status} />
        </div>
      </div>
      {showUserActions && (
        <div className="button-group">
          {showInvite && (
            <div className="invite-button-wrapper">
              <button
                type="button"
                className="partner-user-card__icon-btn"
                aria-label="Resend invite"
                onClick={() => onInvite?.(user)}
                disabled={inviteLoading || tabReadOnly}
              >
                <Iconography icon="envelop" size={IconographySizes.Small} />
              </button>
              {inviteLoading && <Loader variant={LoaderSizes.small} />}
            </div>
          )}
          <Button
            variant="secondary"
            className="user-edit-button"
            icon={
              <Iconography
                icon="userPen"
                size={IconographySizes.Medium}
                theme={IconographyThemes.Info}
              />
            }
            onClick={() => {
              onEditUserClick(user);
            }}
            disabled={tabReadOnly}
          />
          <Toggle
            id={"toggle-" + user.ID}
            onClick={(e: any) => handleToggle(e)}
            toggled={toggled}
            disabled={disabled || tabReadOnly}
          />
        </div>
      )}
    </div>
  );
};

export default PartnerUserCard;
